import { ITimeRange, DayOfWeek } from './datetime'
import { IManager } from './user'

export interface IDatLane {
  id: string
  lane: string
  origin: string
  destination: string
  originReferenceCity: string
  originReferenceState: string
  destinationReferenceCity: string
  destinationReferenceState: string
}

export interface ILaneInfo {
  origin: string
  destination: string
  id: string
}

export interface ICarrier {
  id: string | number
  legalName: string
  state: string
}

export type IDeliveryTimePreference = 'same-day' | 'next-day' | 'any'

export interface ILocation {
  id?: string | number
  city: string
  state: string
  stateCode: string
  countryCode?: string
}

export interface IOfferLimits {
  pickupCity: string
  pickupStateCode: string
  pickupRadius: string | number
  carrierApplicationId: string
  createdAt: string
  createdBy: {
    id: string
    firstName: string
    lastName: string
  }
  createdById: string
  mondayLimit: number
  tuesdayLimit: number
  wednesdayLimit: number
  thursdayLimit: number
  fridayLimit: number
  saturdayLimit: number
  sundayLimit: number
  pickupLocation: {
    stateCode: string
    countryCode: string
    city: string
    radius: number
  }
  id: string
  uuid: string
}

export interface IPickupTimePreference {
  carrierContractLaneUuid?: string
  dayOfWeek: DayOfWeek
  endTime: string | null
  id?: string | number
  startTime: string | null
  checked?: boolean
}

export interface IBand {
  id?: number
  min: number
  max: number
  checked: boolean
  value: string
}

export interface IDispatcher {
  id: number
  parentId: number
  parentUserId: number
  firstName: string
  lastName: string
  phone: string
  cellPhone: string
}

export enum RATE_ACTION_TYPE {
  reserved = 'auto_offer',
  alert = 'lane_alert',
}

export enum IRateType {
  rpm = 'rpm',
  lane = 'lane',
  radius = 'radius',
}

export interface ICarrierCapacity {
  alreadySet?: boolean
  loading: boolean
  values?: {
    mondayLimit: string
    tuesdayLimit: string
    wednesdayLimit: string
    thursdayLimit: string
    fridayLimit: string
    saturdayLimit: string
    sundayLimit: string
  }
  pickupCity?: string
  pickupStateCode?: string
  pickupRadius?: number
  pickupLocation?: {
    city?: string
    stateCode?: string
    countryCode?:
      | {
          code: string
        }
      | string
    radius?: number
  }
  pickupLocationError?: string
  createdBy?: {
    firstName: string
    lastName: string
  }
  id?: string
  _destroy?: boolean
  uuid?: string
  summary?: string
  mondayLimit: string
  tuesdayLimit: string
  wednesdayLimit: string
  thursdayLimit: string
  fridayLimit: string
  saturdayLimit: string
  sundayLimit: string
}

export interface IUser {
  userType: string
  role: string
  firstName: string
  lastName: string
  email: string
  phone: string
  cellPhone: string
}

export interface IRate {
  avgMargin?: number
  approvedAt?: string
  contractId: number
  approvedById?: number
  carrierId: number
  carrierName: string
  rate: string
  pickup: ILocation
  pickupLocation: ILocation
  delivery: ILocation
  carrier: ICarrier
  equipmentId: number
  equipment: {
    id: string | number
    name: string
    key: string
  }
  carrierApplicationOfferLimits?: IOfferLimits
  pickupTimePreferences: IPickupTimePreference[]
  pickupTimeEveryday?: ITimeRange
  pickupTimeType: string
  deliveryTimePreference: IDeliveryTimePreference
  expirationDate: string | null
  startDate: string | null
  createdAt: string
  createdBy: IManager
  dispatcher: IDispatcher
  dispatcherName: string
  dispatcherList: IDispatcher[]
  displayUserCreation?: boolean
  loadType: string
  isOnlyBeer: boolean | null
  isOnlyDropTrailer: boolean | null
  isShipperExclusive: boolean | null
  isDatMarketApplicable: boolean | null
  shipper?: {
    id: string | number
    name: string
  }
  deliveryRadiusRangeMax: number | null
  deliveryRadiusRangeMin: number | null
  pickupRadiusMiles: number | null
  deliveryRadiusMiles: number | null
  actionType: RATE_ACTION_TYPE
  band?: IRate[]
  userPermissions?: {
    canApproveContractRates: boolean
  }
  startType: string
  expirationType: string
  deliveryLocation: ILocation
  carrierCapacity?: ICarrierCapacity
  user?: IUser
  radiusRates: IBand[]
  rateType: IRateType | undefined
  buttonClicked?: boolean
  shipperId?: number
  shipperName?: string
  laneSupportsBandedRates: boolean
  laneId: string | number
}

export interface IRateAPI extends Omit<IRate, 'dispatcher'> {
  dispatcher: number
}

export interface ILaneRate {
  lane: string
  equipmentOrder: number
  equipmentName: string
  equipmentId: number
  rates: IRate[]
}

export interface ICarrierContractLanesState {
  data: {
    datLane?: IDatLane
    laneRates: ILaneRate[]
    radiusRates: ILaneRate[]
  }
  loading: boolean
  error: string
}

export interface IContractLane {
  contractId?: number
  createdAt?: string
  createdBy?: {
    fullName?: string
    isTransfixAdmin: boolean
  }
  actionType?: string
  creationSource?: string
  datMarketLane?: IDatLane
  delivery: Partial<ILocation>
  deliveryRadiusMiles: number
  deliveryRadiusRangeMax?: number
  deliveryRadiusRangeMin?: number
  deliveryTimePreference?: string
  pickup: Partial<ILocation>
  pickupRadiusMiles: number
  pickupTimePreferences?: IPickupTimePreference[]
  equipment: {
    id?: number
    key?: string
    name: string
  }
  rate: string
  rateType: string
  uuid?: string
}
